import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Stack, TextField,
  IconButton, Tooltip, Button
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import useApiService from '../../component/hooks/useApiService';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';

const PUBLIC_ENDPOINT = '/api/trending_pairs';
const ADMIN_ENDPOINT  = '/api/admins/trending_pairs';

const intStr = (v) =>
  (v === null || v === undefined || Number.isNaN(Number(v)))
    ? ''
    : String(Math.max(0, parseInt(v, 10)));

const extractList = (res) => {
  const cands = [res, res?.items, res?.data, res?.data?.items, res?.result, res?.rows];
  for (const c of cands) if (Array.isArray(c)) return c;
  console.warn('Unexpected response shape from /api/trending_pairs:', res);
  return [];
};

const normalizeItem = (it = {}) => ({
  id: it.id ?? it.ID,
  pair: it.pair ?? it.symbol,

  longTradersPct:  it.long_traders_percent ?? it.longTradersPercent ?? it.long_traders_pct,
  shortTradersPct: it.short_traders_percent ?? it.shortTradersPercent ?? it.short_traders_pct,

  longTradersCount:  it.long_traders_count  ?? it.longTradersCount,
  shortTradersCount: it.short_traders_count ?? it.shortTradersCount,

  calculatedAt: it.calculated_at ?? it.calculatedAt,
});

export default function TrendingPairsList() {
  const { executeGet, executePatch, executePut } = useApiService();
  const { showError, showSuccess } = useAppSnackbar?.() ?? {};

  const getRef   = useRef(executeGet);
  const patchRef = useRef(executePatch);
  const putRef   = useRef(executePut);
  useEffect(() => { getRef.current = executeGet; }, [executeGet]);
  useEffect(() => { patchRef.current = executePatch; }, [executePatch]);
  useEffect(() => { putRef.current = executePut; }, [executePut]);

  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getRef.current({ url: PUBLIC_ENDPOINT, params: { count: 200 } });
      const list = extractList(res).map(normalizeItem);
      setRows(list);
      setDrafts(list);
    } catch (e) {
      console.error('Trending pairs fetch failed:', e);
      showError?.('Trending Pairs yüklenemedi.');
      setRows([]); setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const onChange = (idx, field) => (e) => {
    const v = e.target.value;
    setDrafts((cur) => {
      const next = [...cur];
      next[idx] = { ...next[idx], [field]: intStr(v) };
      return next;
    });
  };

  const onChangePair = (idx) => (e) => {
    const v = (e.target.value ?? '').toString().trim().toUpperCase();
    setDrafts((cur) => {
      const next = [...cur];
      next[idx] = { ...next[idx], pair: v };
      return next;
    });
  };

  const resetRow = (idx) =>
    setDrafts((cur) => { const next = [...cur]; next[idx] = { ...rows[idx] }; return next; });

  const rowPatch = useCallback((d, o) => {
    if (!d?.id) return null;

    const lcRaw = d.longTradersCount === '' ? null : Number(d.longTradersCount);
    const scRaw = d.shortTradersCount === '' ? null : Number(d.shortTradersCount);

    const hasLcChange = lcRaw !== null && lcRaw !== Number(o.longTradersCount);
    const hasScChange = scRaw !== null && scRaw !== Number(o.shortTradersCount);

    const payload = {};
    const newPair = (d.pair ?? '').toString().trim();
    if (newPair && newPair !== (o.pair ?? '').toString().trim()) {
      payload.pair = newPair;
    }

    if (hasLcChange) payload.long_traders_count  = lcRaw;
    if (hasScChange) payload.short_traders_count = scRaw;

    if (hasLcChange || hasScChange) {
      const lc = hasLcChange ? lcRaw : Number(o.longTradersCount)  || 0;
      const sc = hasScChange ? scRaw : Number(o.shortTradersCount) || 0;
      const tot = lc + sc;
      if (tot > 0) {
        const lp = Math.round((lc / tot) * 100);
        payload.long_traders_percent  = lp;
        payload.short_traders_percent = 100 - lp;
      }
    }

    return Object.keys(payload).length ? payload : null;
  }, []);

  const patches = useMemo(
    () => drafts.map((d, i) => rowPatch(d, rows[i])),
    [drafts, rows, rowPatch]
  );
  const dirtyIndices = useMemo(
    () => patches.map((p, i) => (p ? i : null)).filter((v) => v !== null),
    [patches]
  );
  const dirtyCount = dirtyIndices.length;

  const saveAll = async () => {
    if (dirtyCount === 0) { showSuccess?.('Değişiklik yok.'); return; }
    try {
      setSavingAll(true);
      const call = patchRef.current ?? putRef.current;

      const requests = dirtyIndices.map((i) =>
        call({ url: `${ADMIN_ENDPOINT}/${drafts[i].id}`, data: patches[i] })
      );
      const results = await Promise.allSettled(requests);

      const ok = results.filter(r => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      if (ok) showSuccess?.(`${ok} kayıt kaydedildi.`);
      if (fail) showError?.(`${fail} kayıt kaydedilemedi.`);

      await load();
    } catch (e) {
      console.error('Bulk save failed:', e);
      showError?.('Kaydetme başarısız.');
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <Box p={4} sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight={600}>Trendler</Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDrafts(rows)}
            disabled={dirtyCount === 0 || savingAll}
          >
            Tümünü Geri Al
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={saveAll}
            disabled={dirtyCount === 0 || savingAll}
          >
            Hepsini Kaydet{dirtyCount ? ` (${dirtyCount})` : ''}
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} elevation={2} sx={{ width: '100%' }}>
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Pair</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 14 }}>Long Traders</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 14 }}>Short Traders</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 14 }}>Hesaplanma Tarihi</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 14 }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box py={3}><CircularProgress size={28} /></Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">Kayıt bulunamadı</TableCell>
              </TableRow>
            ) : (
              drafts.map((r, idx) => {
                const dirty = !!patches[idx];
                return (
                  <TableRow key={r.id ?? `${r.pair}-${r.calculatedAt}`} hover selected={dirty}>
                    <TableCell sx={{ fontSize: 15, py: 1.5 }}>
                      <TextField
                        value={r.pair ?? ''}
                        onChange={onChangePair(idx)}
                        placeholder="EUR/USD"
                        size="small"
                        inputProps={{ style: { textTransform: 'uppercase', width: 160 } }}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ fontSize: 15, py: 1.5 }}>
                      <TextField
                        value={r.longTradersCount ?? ''}
                        onChange={onChange(idx, 'longTradersCount')}
                        type="number"
                        inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 140 } }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 15, py: 1.5 }}>
                      <TextField
                        value={r.shortTradersCount ?? ''}
                        onChange={onChange(idx, 'shortTradersCount')}
                        type="number"
                        inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 140 } }}
                        size="small"
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ fontSize: 15, py: 2 }}>
                      {r.calculatedAt ? new Date(r.calculatedAt).toLocaleString() : '-'}
                    </TableCell>

                    <TableCell align="center" sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Satırı geri al">
                        <IconButton size="small" onClick={() => resetRow(idx)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
